import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RELEASE_NOTES, RELEASE_NOTE_CATEGORIES } from '../data/releaseNotes';
import { useDebounce } from '../hooks/useDebounce';

const ReleaseNotesPage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set(RELEASE_NOTES[0] ? [RELEASE_NOTES[0].month] : []));
  const debouncedSearch = useDebounce(search, 350);
  const term = debouncedSearch.trim().toLowerCase();
  const filtering = term.length > 0 || category !== 'all';

  const visibleReleases = useMemo(() => RELEASE_NOTES
    .map(release => {
      const groups = RELEASE_NOTE_CATEGORIES
        .filter(cat => category === 'all' || cat.key === category)
        .map(cat => ({
          ...cat,
          items: (release[cat.key] || []).filter(item => !term || item.toLowerCase().includes(term)),
        }))
        .filter(group => group.items.length > 0);
      return { ...release, groups, total: groups.reduce((sum, group) => sum + group.items.length, 0) };
    })
    .filter(release => release.total > 0), [term, category]);

  const toggle = (month) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(month)) next.delete(month); else next.add(month);
    return next;
  });

  const allExpanded = visibleReleases.length > 0 && visibleReleases.every(r => expanded.has(r.month));

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/support" className="text-sm text-indigo-600 hover:text-indigo-700">← Help Center</Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-2 mb-2">Release Notes</h1>
          <p className="text-gray-600">New features, improvements and bug fixes, by month.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            aria-label="Search release notes"
            placeholder="Search release notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`flex-1 min-w-0 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${search ? 'border-yellow-400 border-2' : 'border-gray-300'}`}
          />
          <select
            aria-label="Filter by type"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
          >
            <option value="all">All changes</option>
            {RELEASE_NOTE_CATEGORIES.map(cat => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </select>
          <button
            type="button"
            title={allExpanded ? 'Collapse all months' : 'Expand all months'}
            onClick={() => setExpanded(allExpanded ? new Set() : new Set(visibleReleases.map(r => r.month)))}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>

        {visibleReleases.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow text-gray-600">
            No release notes match your search.
          </div>
        ) : (
          <div className="space-y-4">
            {visibleReleases.map(release => {
              const isOpen = filtering || expanded.has(release.month);
              return (
                <section key={release.month} className="bg-white rounded-lg shadow">
                  <button
                    type="button"
                    onClick={() => toggle(release.month)}
                    aria-expanded={isOpen}
                    title={isOpen ? 'Collapse' : 'Expand'}
                    className="w-full px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <h2 className="text-xl font-semibold text-gray-900">{release.title}</h2>
                      {release.summary && <p className="text-sm text-gray-600 mt-1">{release.summary}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {release.groups.map(group => (
                        <span key={group.key} title={group.label} className={`hidden sm:inline-flex text-xs px-2 py-0.5 rounded-full border ${group.className}`}>
                          {group.icon} {group.items.length}
                        </span>
                      ))}
                      <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 space-y-4 border-t border-gray-100 pt-4">
                      {release.groups.map(group => (
                        <div key={group.key}>
                          <h3 className={`inline-flex items-center gap-1 text-sm font-semibold px-2 py-0.5 rounded border ${group.className}`}>
                            {group.icon} {group.label}
                          </h3>
                          <ul className="mt-2 ml-5 list-disc space-y-1 text-gray-700 text-sm">
                            {group.items.map(item => <li key={item}>{item}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default ReleaseNotesPage;
